import {
  AudioFingerprint,
  BrowserFingerprint,
  CanvasFingerprint,
  DeviceInfo,
  FingerprintCollectionResult,
  FingerprintCollectionStatus,
  FingerprintData,
  ScreenFingerprint,
  TimezoneFingerprint,
  fingerprintDataSchema,
} from "@/core/dtos/fingerprint-data";

export async function collectFingerprint(): Promise<FingerprintCollectionResult> {
  const startTime = performance.now();

  try {
    const [browser, screen, timezone, canvas, audio, device] =
      await Promise.allSettled([
        collectBrowserFingerprint(),
        collectScreenFingerprint(),
        collectTimezoneFingerprint(),
        collectCanvasFingerprint(),
        collectAudioFingerprint(),
        collectDeviceInfo(),
      ]);

    const browserData =
      browser.status === "fulfilled"
        ? browser.value
        : getDefaultBrowserFingerprint();

    const screenData =
      screen.status === "fulfilled"
        ? screen.value
        : getDefaultScreenFingerprint();

    const timezoneData =
      timezone.status === "fulfilled"
        ? timezone.value
        : getDefaultTimezoneFingerprint();

    const canvasData =
      canvas.status === "fulfilled"
        ? canvas.value
        : getDefaultCanvasFingerprint();

    const audioData =
      audio.status === "fulfilled" ? audio.value : getDefaultAudioFingerprint();

    const deviceData =
      device.status === "fulfilled" ? device.value : getDefaultDeviceInfo();

    const fingerprintDataWithoutHash = {
      browser: browserData,
      screen: screenData,
      timezone: timezoneData,
      canvas: canvasData,
      audio: audioData,
      device: deviceData,
      collectedAt: new Date(),
    };

    const hash = await generateFingerprintHash(fingerprintDataWithoutHash);

    const fingerprintData: FingerprintData = {
      ...fingerprintDataWithoutHash,
      hash,
    };

    const collectionTimeMs = performance.now() - startTime;

    return {
      status: FingerprintCollectionStatus.COMPLETED,
      data: fingerprintData,
      collectionTimeMs,
    };
  } catch (error) {
    const collectionTimeMs = performance.now() - startTime;
    console.error("Fingerprint collection failed:", error);

    return {
      status: FingerprintCollectionStatus.FAILED,
      error: error instanceof Error ? error.message : "Unknown error occurred",
      collectionTimeMs,
    };
  }
}

export function parseFingerprintData(payload: {
  fingerprint: unknown;
}): FingerprintData | null {
  const { fingerprint } = payload;

  if (!fingerprint || typeof fingerprint !== "object") {
    return null;
  }

  const rawFingerprint = fingerprint as Record<string, unknown>;
  const normalizedFingerprint = {
    ...rawFingerprint,
    collectedAt:
      typeof rawFingerprint.collectedAt === "string" ||
      typeof rawFingerprint.collectedAt === "number"
        ? new Date(rawFingerprint.collectedAt)
        : rawFingerprint.collectedAt,
  };

  const validationResult = fingerprintDataSchema.safeParse(normalizedFingerprint);

  if (!validationResult.success) {
    return null;
  }

  return validationResult.data;
}

async function collectBrowserFingerprint(): Promise<BrowserFingerprint> {
  const nav = navigator;
  const availableFonts = await detectAvailableFonts();

  return {
    userAgent: nav.userAgent,
    language: nav.language,
    languages: Array.from(nav.languages),
    platform: nav.platform,
    cookieEnabled: nav.cookieEnabled,
    doNotTrack: nav.doNotTrack || null,
    hardwareConcurrency: nav.hardwareConcurrency || 0,
    maxTouchPoints: nav.maxTouchPoints || 0,
    vendor: nav.vendor || "",
    vendorSub: nav.vendorSub || "",
    productSub: nav.productSub || "",
    oscpu: (nav as Navigator & { oscpu?: string }).oscpu,
    availableFonts,
  };
}

async function detectAvailableFonts(): Promise<string[]> {
  try {
    const fontsToTest = [
      "Abadi MT Condensed Light",
      "Adobe Fangsong Std",
      "Adobe Hebrew",
      "Adobe Ming Std",
      "Agency FB",
      "Aharoni",
      "Andalus",
      "Angsana New",
      "AngsanaUPC",
      "Aparajita",
      "Arab",
      "Arabic Transparent",
      "Arabic Typesetting",
      "Arial Baltic",
      "Arial Black",
      "Arial CE",
      "Arial CYR",
      "Arial Greek",
      "Arial TUR",
      "Arial",
      "Batang",
      "BatangChe",
      "Bauhaus 93",
      "Bell MT",
      "Bitstream Vera Serif",
      "Bodoni MT",
      "Bookman Old Style",
      "Braggadocio",
      "Broadway",
      "Browallia New",
      "BrowalliaUPC",
      "Calibri Light",
      "Calibri",
      "Californian FB",
      "Cambria Math",
      "Cambria",
      "Candara",
      "Castellar",
      "Casual",
      "Centaur",
      "Century Gothic",
      "Chalkduster",
      "Colonna MT",
      "Comic Sans MS",
      "Consolas",
      "Constantia",
      "Copperplate Gothic Light",
      "Corbel",
      "Cordia New",
      "CordiaUPC",
      "Courier New Baltic",
      "Courier New CE",
      "Courier New CYR",
      "Courier New Greek",
      "Courier New TUR",
      "Courier New",
      "DFKai-SB",
      "DaunPenh",
      "David",
      "DejaVu LGC Sans Mono",
      "Desdemona",
      "DilleniaUPC",
      "DokChampa",
      "Dotum",
      "DotumChe",
      "Ebrima",
      "Engravers MT",
      "Eras Bold ITC",
      "Estrangelo Edessa",
      "EucrosiaUPC",
      "Euphemia",
      "Eurostile",
      "FangSong",
      "Forte",
      "FrankRuehl",
      "Franklin Gothic Heavy",
      "Franklin Gothic Medium",
      "FreesiaUPC",
      "French Script MT",
      "Gabriola",
      "Gautami",
      "Georgia",
      "Gigi",
      "Gisha",
      "Goudy Old Style",
      "Gulim",
      "GulimChe",
      "GungSeo",
      "Gungsuh",
      "GungsuhChe",
      "Haettenschweiler",
      "Harrington",
      "Hei S",
      "HeiT",
      "Heisei Kaku Gothic",
      "Hiragino Sans GB",
      "Impact",
      "Informal Roman",
      "IrisUPC",
      "Iskoola Pota",
      "JasmineUPC",
      "KacstOne",
      "KaiTi",
      "Kalinga",
      "Kartika",
      "Khmer UI",
      "Kino MT",
      "KodchiangUPC",
      "Kokila",
      "Kozuka Gothic Pr6N",
      "Lao UI",
      "Latha",
      "Leelawadee",
      "Levenim MT",
      "LilyUPC",
      "Lohit Gujarati",
      "Loma",
      "Lucida Bright",
      "Lucida Console",
      "Lucida Fax",
      "Lucida Sans Unicode",
      "MS Gothic",
      "MS Mincho",
      "MS PGothic",
      "MS PMincho",
      "MS Reference Sans Serif",
      "MS UI Gothic",
      "MV Boli",
      "Magneto",
      "Malgun Gothic",
      "Mangal",
      "Marlett",
      "Matura MT Script Capitals",
      "Meiryo UI",
      "Meiryo",
      "Menlo",
      "Microsoft Himalaya",
      "Microsoft JhengHei",
      "Microsoft New Tai Lue",
      "Microsoft PhagsPa",
      "Microsoft Sans Serif",
      "Microsoft Tai Le",
      "Microsoft Uighur",
      "Microsoft YaHei",
      "Microsoft Yi Baiti",
      "MingLiU",
      "MingLiU-ExtB",
      "MingLiU_HKSCS",
      "MingLiU_HKSCS-ExtB",
      "Miriam Fixed",
      "Miriam",
      "Mongolian Baiti",
      "MoolBoran",
      "NSimSun",
      "Narkisim",
      "News Gothic MT",
      "Niagara Solid",
      "Nyala",
      "PMingLiU",
      "PMingLiU-ExtB",
      "Palace Script MT",
      "Palatino Linotype",
      "Papyrus",
      "Perpetua",
      "Plantagenet Cherokee",
      "Playbill",
      "Prelude Bold",
      "Prelude Condensed Bold",
      "Prelude Condensed Medium",
      "Prelude Medium",
      "PreludeCompressedWGL Black",
      "PreludeCompressedWGL Bold",
      "PreludeCompressedWGL Light",
      "PreludeCompressedWGL Medium",
      "PreludeCondensedWGL Black",
      "PreludeCondensedWGL Bold",
      "PreludeCondensedWGL Light",
      "PreludeCondensedWGL Medium",
      "PreludeWGL Black",
      "PreludeWGL Bold",
      "PreludeWGL Light",
      "PreludeWGL Medium",
      "Raavi",
      "Rachana",
      "Rockwell",
      "Rod",
      "Sakkal Majalla",
      "Sawasdee",
      "Script MT Bold",
      "Segoe Print",
      "Segoe Script",
      "Segoe UI Light",
      "Segoe UI Semibold",
      "Segoe UI Symbol",
      "Segoe UI",
      "Shonar Bangla",
      "Showcard Gothic",
      "Shruti",
      "SimHei",
      "SimSun",
      "SimSun-ExtB",
      "Simplified Arabic Fixed",
      "Simplified Arabic",
      "Snap ITC",
      "Sylfaen",
      "Symbol",
      "Tahoma",
      "Times New Roman Baltic",
      "Times New Roman CE",
      "Times New Roman CYR",
      "Times New Roman Greek",
      "Times New Roman TUR",
      "Times New Roman",
      "TlwgMono",
      "Traditional Arabic",
      "Trebuchet MS",
      "Tunga",
      "Tw Cen MT Condensed Extra Bold",
      "Ubuntu",
      "Umpush",
      "Univers",
      "Utopia",
      "Utsaah",
      "Vani",
      "Verdana",
      "Vijaya",
      "Vladimir Script",
      "Vrinda",
      "Webdings",
      "Wide Latin",
      "Wingdings",
    ];

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return [];

    const testString = "mmmmmmmmmmlli";
    const testSize = "72px";
    const baseFonts = ["monospace", "sans-serif", "serif"];
    const availableFonts: string[] = [];

    const baselineWidths: { [key: string]: number } = {};
    for (const baseFont of baseFonts) {
      context.font = `${testSize} ${baseFont}`;
      baselineWidths[baseFont] = context.measureText(testString).width;
    }

    for (const font of fontsToTest) {
      let detected = false;

      for (const baseFont of baseFonts) {
        context.font = `${testSize} ${font}, ${baseFont}`;
        const width = context.measureText(testString).width;

        if (width !== baselineWidths[baseFont]) {
          detected = true;
          break;
        }
      }

      if (detected) {
        availableFonts.push(font);
      }
    }

    return availableFonts.sort();
  } catch (error) {
    console.warn("Font detection failed:", error);
    return [];
  }
}

async function collectScreenFingerprint(): Promise<ScreenFingerprint> {
  const screen = window.screen;

  return {
    width: screen.width,
    height: screen.height,
    availWidth: screen.availWidth,
    availHeight: screen.availHeight,
    colorDepth: screen.colorDepth,
    pixelDepth: screen.pixelDepth,
    orientation: screen.orientation
      ? {
          angle: screen.orientation.angle,
          type: screen.orientation.type,
        }
      : undefined,
  };
}

async function collectTimezoneFingerprint(): Promise<TimezoneFingerprint> {
  const date = new Date();

  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: date.getTimezoneOffset(),
    locale: Intl.DateTimeFormat().resolvedOptions().locale,
  };
}

async function collectCanvasFingerprint(): Promise<CanvasFingerprint> {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return { canvasFingerprint: "canvas_not_supported" };
  }

  canvas.width = 200;
  canvas.height = 50;

  ctx.textBaseline = "top";
  ctx.font = "14px 'Arial'";
  ctx.fillStyle = "#f60";
  ctx.fillRect(125, 1, 62, 20);
  ctx.fillStyle = "#069";
  ctx.fillText("TMM Fingerprint 🛡️", 2, 15);
  ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
  ctx.fillText("TMM Fingerprint 🛡️", 4, 17);

  const canvasFingerprint = canvas.toDataURL();

  let webglFingerprint: string | undefined;
  try {
    const gl =
      canvas.getContext("webgl") ||
      (canvas.getContext("experimental-webgl") as WebGLRenderingContext | null);
    if (gl) {
      const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
      if (debugInfo) {
        const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        webglFingerprint = `${vendor}~${renderer}`;
      }
    }
  } catch (e) {
    console.warn("WebGL fingerprint collection failed:", e);
  }

  return {
    canvasFingerprint,
    webglFingerprint,
  };
}

async function collectAudioFingerprint(): Promise<AudioFingerprint> {
  try {
    const audioContext = new (window.AudioContext ||
      (window as Window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext!)();
    const oscillator = audioContext.createOscillator();
    const analyser = audioContext.createAnalyser();
    const gainNode = audioContext.createGain();
    const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);

    gainNode.gain.value = 0;
    oscillator.type = "triangle";
    oscillator.frequency.value = 10000;

    oscillator.connect(analyser);
    analyser.connect(scriptProcessor);
    scriptProcessor.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start(0);

    return new Promise((resolve) => {
      let isResolved = false;

      const cleanup = () => {
        if (isResolved) return;
        isResolved = true;

        try {
          if (oscillator) {
            oscillator.stop();
          }
        } catch (e) {
          console.warn("Error stopping oscillator:", e);
        }

        try {
          if (scriptProcessor) {
            scriptProcessor.disconnect();
          }
        } catch (e) {
          console.warn("Error disconnecting script processor:", e);
        }

        try {
          if (audioContext && audioContext.state !== "closed") {
            audioContext.close();
          }
        } catch (e) {
          console.warn("Error closing audio context:", e);
        }
      };

      scriptProcessor.onaudioprocess = (event) => {
        if (isResolved) return;

        const buffer = event.inputBuffer.getChannelData(0);
        let sum = 0;
        for (let i = 0; i < buffer.length; i++) {
          sum += Math.abs(buffer[i]);
        }
        const audioFingerprint = sum.toString();

        cleanup();
        resolve({ audioFingerprint });
      };

      setTimeout(() => {
        if (!isResolved) {
          cleanup();
          resolve({ audioFingerprint: undefined });
        }
      }, 1000);
    });
  } catch {
    return { audioFingerprint: undefined };
  }
}

async function collectDeviceInfo(): Promise<DeviceInfo> {
  type NetworkInformationLike = {
    type?: string;
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
  };
  const nav = navigator as Navigator & {
    deviceMemory?: number;
    connection?: NetworkInformationLike;
    mozConnection?: NetworkInformationLike;
    webkitConnection?: NetworkInformationLike;
  };
  const connection =
    nav.connection || nav.mozConnection || nav.webkitConnection;

  return {
    deviceMemory: nav.deviceMemory,
    connectionType: connection?.type,
    connectionEffectiveType: connection?.effectiveType,
    connectionDownlink: connection?.downlink,
    connectionRtt: connection?.rtt,
  };
}

async function generateFingerprintHash(
  data: Omit<FingerprintData, "hash" | "collectedAt">,
): Promise<string> {
  const jsonString = JSON.stringify(data, Object.keys(data).sort());
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(jsonString);
  const hashBuffer = await crypto.subtle.digest("SHA-256", dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

function getDefaultBrowserFingerprint(): BrowserFingerprint {
  return {
    userAgent: "unknown",
    language: "unknown",
    languages: [],
    platform: "unknown",
    cookieEnabled: false,
    doNotTrack: null,
    hardwareConcurrency: 0,
    maxTouchPoints: 0,
    vendor: "",
    vendorSub: "",
    productSub: "",
    availableFonts: [],
  };
}

function getDefaultScreenFingerprint(): ScreenFingerprint {
  return {
    width: 0,
    height: 0,
    availWidth: 0,
    availHeight: 0,
    colorDepth: 0,
    pixelDepth: 0,
  };
}

function getDefaultTimezoneFingerprint(): TimezoneFingerprint {
  return {
    timezone: "unknown",
    timezoneOffset: 0,
    locale: "unknown",
  };
}

function getDefaultCanvasFingerprint(): CanvasFingerprint {
  return {
    canvasFingerprint: "canvas_error",
  };
}

function getDefaultAudioFingerprint(): AudioFingerprint {
  return {
    audioFingerprint: undefined,
  };
}

function getDefaultDeviceInfo(): DeviceInfo {
  return {
    deviceMemory: undefined,
    connectionType: undefined,
    connectionEffectiveType: undefined,
    connectionDownlink: undefined,
    connectionRtt: undefined,
  };
}

export function generateSessionFingerprintId(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `fp_${timestamp}_${random}`;
}

export function isFingerprintingSupported(): boolean {
  return !!(window.crypto && window.crypto.subtle && window.performance);
}
